import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Database, 
  BookOpen, 
  AlertTriangle,
  ExternalLink,
  ShieldCheck
} from 'lucide-react';

const translations = {
  sv: {
    back: '← Tillbaka',
    pageTitle: 'Metodik & datakällor',
    pageSubtitle: 'Hur Skogskalkyl räknar och vad resultaten betyder',
    
    sourcesTitle: 'Datakällor vi använder',
    sources: [
      {
        name: 'SLU Riksskogstaxeringen',
        desc: 'Sveriges officiella skogsinventering, utförd sedan 1923. Ger tillförlitliga uppskattningar av tillväxt, volym och beståndsegenskaper för svenska skogar.',
        url: 'https://www.slu.se/riksskogstaxeringen'
      },
      {
        name: 'Skogsstyrelsens prisstatistik',
        desc: 'Officiell statistik över virkesprisnivåer per region, sortiment och kvalitet. Uppdateras regelbundet baserat på faktiska transaktioner.',
        url: 'https://www.skogsstyrelsen.se/statistik/'
      },
      {
        name: 'Skogforsk',
        desc: 'Branschens forskningsinstitut. Ger underlag för kostnadsmodeller, avverkningsmetoder och produktivitetsantaganden.',
        url: 'https://www.skogforsk.se/'
      }
    ],
    
    methodTitle: 'Hur beräkningarna fungerar',
    methodItems: [
      {
        title: 'Modellbaserade uppskattningar',
        text: 'Våra kalkylatorer använder etablerade skogsekonomiska modeller som kombinerar dina inmatningar med statistiska genomsnitt och regionala data.'
      },
      {
        title: 'Osäkerhet redovisas',
        text: 'Där det är möjligt visar vi inte bara ett värde utan även ett troligt spann. Verkligheten varierar — vår uppgift är att ge dig en rimlig utgångspunkt.'
      },
      {
        title: 'Antaganden om marknaden',
        text: 'Priser och kostnader baseras på aktuella genomsnitt. Faktiska priser vid försäljning kan variera beroende på marknadslage, kvalitet och köpare.'
      }
    ],
    
    limitationsTitle: 'Vad resultaten inte är',
    limitationsText: 'Skogskalkyl är ett beslutsstöd — inte ett värderingsintyg eller juridiskt bindande dokument.',
    limitationsItems: [
      'Ersätter inte en professionell värdering vid försäljning eller rättsliga ärenden',
      'Bygger på genomsnitt och kan inte fånga alla lokala variationer',
      'Är inte en garanti för framtida priser eller intäkter',
      'Ska användas som underlag för diskussion, inte som slutgiltigt facit'
    ],
    
    purposeTitle: 'Vad Skogskalkyl är till för',
    purposeText: 'Att hjälpa dig gå från osäker siffra till bättre beslut. Med Skogskalkyl kan du:',
    purposeItems: [
      'Få en första uppskattning innan du kontaktar mäklare eller rådgivare',
      'Förstå vad som påverkar värdet på din skog',
      'Jämföra olika alternativ och scenarier',
      'Förbereda dig för diskussioner med professionella aktörer'
    ],
    
    ctaText: 'Har du frågor om hur vi räknar?',
    ctaButton: 'Kontakta oss'
  },
  de: {
    back: '← Zurück',
    pageTitle: 'Methodik & Datenquellen',
    pageSubtitle: 'Wie Skogskalkyl rechnet und was die Ergebnisse bedeuten',
    
    sourcesTitle: 'Unsere Datenquellen',
    sources: [
      {
        name: 'SLU Riksskogstaxeringen',
        desc: 'Schwedens offizielle Waldinventur seit 1923. Liefert zuverlässige Schätzungen zu Wachstum, Volumen und Bestandseigenschaften.',
        url: 'https://www.slu.se/riksskogstaxeringen'
      },
      {
        name: 'Skogsstyrelsens Preisstatistik',
        desc: 'Offizielle Statistik zu Holzpreisniveaus nach Region, Sortiment und Qualität.',
        url: 'https://www.skogsstyrelsen.se/statistik/'
      },
      {
        name: 'Skogforsk',
        desc: 'Branchenforschungsinstitut. Grundlage für Kostenmodelle und Produktivitätsannahmen.',
        url: 'https://www.skogforsk.se/'
      }
    ],
    
    methodTitle: 'Wie die Berechnungen funktionieren',
    methodItems: [
      {
        title: 'Modellbasierte Schätzungen',
        text: 'Unsere Rechner verwenden etablierte forstwirtschaftliche Modelle, die Ihre Eingaben mit statistischen Durchschnittswerten kombinieren.'
      },
      {
        title: 'Unsicherheit wird ausgewiesen',
        text: 'Wo möglich zeigen wir nicht nur einen Wert, sondern auch eine wahrscheinliche Spanne.'
      },
      {
        title: 'Marktannahmen',
        text: 'Preise basieren auf aktuellen Durchschnittswerten. Tatsächliche Preise können variieren.'
      }
    ],
    
    limitationsTitle: 'Was die Ergebnisse nicht sind',
    limitationsText: 'Skogskalkyl ist eine Entscheidungshilfe — kein Wertgutachten oder rechtlich bindendes Dokument.',
    limitationsItems: [
      'Ersetzt keine professionelle Bewertung bei Verkauf oder Rechtsangelegenheiten',
      'Basiert auf Durchschnittswerten und erfasst nicht alle lokalen Variationen',
      'Keine Garantie für zukünftige Preise oder Erträge',
      'Als Diskussionsgrundlage gedacht, nicht als endgültiges Urteil'
    ],
    
    purposeTitle: 'Wofür Skogskalkyl gedacht ist',
    purposeText: 'Ihnen zu helfen, von unsicheren Zahlen zu besseren Entscheidungen zu kommen:',
    purposeItems: [
      'Eine erste Einschätzung erhalten, bevor Sie Makler oder Berater kontaktieren',
      'Verstehen, was den Wert Ihres Waldes beeinflusst',
      'Verschiedene Alternativen und Szenarien vergleichen',
      'Sich auf Gespräche mit Fachleuten vorbereiten'
    ],
    
    ctaText: 'Fragen zu unserer Methodik?',
    ctaButton: 'Kontaktieren Sie uns'
  },
  en: {
    back: '← Back',
    pageTitle: 'Methodology & Data Sources',
    pageSubtitle: 'How Skogskalkyl calculates and what the results mean',
    
    sourcesTitle: 'Data sources we use',
    sources: [
      {
        name: 'SLU National Forest Inventory',
        desc: 'Sweden\'s official forest inventory since 1923. Provides reliable estimates of growth, volume and stand characteristics.',
        url: 'https://www.slu.se/riksskogstaxeringen'
      },
      {
        name: 'Swedish Forest Agency Pricing',
        desc: 'Official statistics on timber price levels by region, assortment and quality.',
        url: 'https://www.skogsstyrelsen.se/statistik/'
      },
      {
        name: 'Skogforsk',
        desc: 'Industry research institute. Basis for cost models and productivity assumptions.',
        url: 'https://www.skogforsk.se/'
      }
    ],
    
    methodTitle: 'How the calculations work',
    methodItems: [
      {
        title: 'Model-based estimates',
        text: 'Our calculators use established forestry models that combine your inputs with statistical averages and regional data.'
      },
      {
        title: 'Uncertainty is reported',
        text: 'Where possible, we show not just a value but also a likely range.'
      },
      {
        title: 'Market assumptions',
        text: 'Prices are based on current averages. Actual prices may vary depending on market conditions.'
      }
    ],
    
    limitationsTitle: 'What the results are not',
    limitationsText: 'Skogskalkyl is decision support — not a valuation certificate or legally binding document.',
    limitationsItems: [
      'Does not replace professional valuation for sales or legal matters',
      'Based on averages and cannot capture all local variations',
      'Not a guarantee of future prices or returns',
      'Intended as a basis for discussion, not a final verdict'
    ],
    
    purposeTitle: 'What Skogskalkyl is for',
    purposeText: 'To help you go from uncertain numbers to better decisions:',
    purposeItems: [
      'Get an initial estimate before contacting brokers or advisors',
      'Understand what affects the value of your forest',
      'Compare different alternatives and scenarios',
      'Prepare for discussions with professionals'
    ],
    
    ctaText: 'Questions about our methodology?',
    ctaButton: 'Contact us'
  }
};

const Metodik = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.sv;

  return (
    <div className="space-y-12 max-w-4xl mx-auto" data-testid="metodik-page">
      {/* Back Link */}
      <Link to="/" className="inline-flex items-center text-forest-600 hover:text-forest-800 transition-colors">
        {t.back}
      </Link>

      {/* Header */}
      <div className="text-center space-y-3">
        <h1 className="text-3xl md:text-4xl font-bold text-forest-800">{t.pageTitle}</h1>
        <p className="text-lg text-muted-foreground">{t.pageSubtitle}</p>
      </div>

      {/* Data Sources */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold text-forest-800 flex items-center gap-2">
          <Database className="h-5 w-5" />
          {t.sourcesTitle}
        </h2>
        <div className="grid gap-4">
          {t.sources.map((source, i) => (
            <Card key={i} className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <h3 className="font-semibold text-forest-800">{source.name}</h3>
                    <p className="text-sm text-slate-600 mt-1">{source.desc}</p>
                  </div>
                  <a 
                    href={source.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-shrink-0 text-forest-600 hover:text-forest-800"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Methodology */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold text-forest-800 flex items-center gap-2">
          <BookOpen className="h-5 w-5" />
          {t.methodTitle}
        </h2>
        <div className="grid gap-4">
          {t.methodItems.map((item, i) => (
            <div key={i} className="bg-slate-50 rounded-lg p-5">
              <h3 className="font-semibold text-forest-800 mb-2">{item.title}</h3>
              <p className="text-sm text-slate-600">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Limitations */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold text-forest-800 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          {t.limitationsTitle}
        </h2>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-5 space-y-4">
            <p className="text-amber-900 font-medium">{t.limitationsText}</p>
            <ul className="space-y-2">
              {t.limitationsItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                  <span className="text-amber-500 mt-1">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Purpose */}
      <section className="space-y-4">
        <h2 className="text-xl font-bold text-forest-800 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" />
          {t.purposeTitle}
        </h2>
        <Card className="border-forest-200 bg-forest-50/50">
          <CardContent className="p-5 space-y-4">
            <p className="text-forest-800">{t.purposeText}</p>
            <ul className="space-y-2">
              {t.purposeItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-forest-700">
                  <span className="text-forest-500 mt-1">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* CTA */}
      <section className="text-center py-8 border-t border-slate-200">
        <p className="text-slate-600 mb-4">{t.ctaText}</p>
        <Link to="/om-oss">
          <Button variant="outline" className="border-forest-300 text-forest-700 hover:bg-forest-50">
            {t.ctaButton}
          </Button>
        </Link>
      </section>
    </div>
  );
};

export default Metodik;
